import React, { useState, useEffect } from 'react';
import './App.css';
import ChatBox from './components/ChatBox';
import ChatInput from './components/ChatInput';
import ConversationList from './components/ConversationList';
import Settings from './components/Settings';
import SettingIcon from './components/SettingIcon';
import logo from './logo.png';

function App() {
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(() => {
    // Load theme from localStorage or default to 'light'
    return localStorage.getItem('theme') || 'light';
  });
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Apply theme to body
  useEffect(() => {
    if (theme === 'dark') {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
    // Save theme preference
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Initialize with a default conversation
  useEffect(() => {
    const defaultId = Date.now().toString();
    const defaultConv = {
      id: defaultId,
      title: 'New Conversation',
      preview: '',
      messages: [{ parts: [{ type: 'text', data: 'Hello! How can I help you today?' }], sender: 'bot' }]
    };
    setConversations([defaultConv]);
    setCurrentConversationId(defaultId);
  }, []);

  const getCurrentConversation = () => {
    return conversations.find(conv => conv.id === currentConversationId);
  };

  const updateConversation = (id, updates) => {
    setConversations(prev => prev.map(conv =>
      conv.id === id ? { ...conv, ...updates } : conv
    ));
  };

  const startNewChat = () => {
    const newId = Date.now().toString();
    const newConv = {
      id: newId,
      title: 'New Conversation',
      preview: '',
      messages: [{ parts: [{ type: 'text', data: 'Hello! How can I help you today?' }], sender: 'bot' }]
    };
    setConversations(prev => [newConv, ...prev]);
    setCurrentConversationId(newId);
  };

  const selectConversation = (id) => {
    setCurrentConversationId(id);
  };

  const sendMessage = async (parts) => {
    if (!currentConversationId) return;

    const currentConv = getCurrentConversation();
    if (!currentConv) return;

    // Add user message
    const userMessage = { parts, sender: 'user' };
    const updatedMessages = [...currentConv.messages, userMessage];
    updateConversation(currentConversationId, { messages: updatedMessages });

    // Update preview with first text part
    const firstTextPart = parts.find(p => p.type === 'text');
    if (firstTextPart) {
      const preview = firstTextPart.data.substring(0, 50);
      updateConversation(currentConversationId, { preview });
    }

    // Update title if it's still "New Conversation"
    if (currentConv.title === 'New Conversation' && parts.length > 0) {
      const firstTextPart = parts.find(p => p.type === 'text');
      if (firstTextPart) {
        const title = firstTextPart.data.substring(0, 30);
        updateConversation(currentConversationId, { title });
      }
    }

    setLoading(true);

    const formData = new FormData();
    parts.forEach(part => {
      formData.append('parts[]', part.data);
    });

    // Prepare history
    const history = currentConv.messages.map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'model',
      parts: msg.parts.map(p => {
        if (p.type === 'image') {
          // We don't send back base64 images in history to save bandwidth/complexity for now
          // unless the backend specifically needs them for context. 
          // For this implementation, let's assume text-only history or handle images carefully.
          // Actually, the backend expects 'parts' to be a list of strings or images.
          // Sending back full base64 images might be heavy. 
          // Let's filter out images from history for now to avoid 413s or massive requests, 
          // or if the user wants full context, we might need to handle it.
          // Given the prompt "include the previous conversation", text is usually sufficient.
          // But if we want to be robust, we should probably skip images in history for this iteration
          // or send them if they are small. 
          // Let's stick to text parts for history to be safe and simple.
          return null;
        }
        return p.data;
      }).filter(Boolean)
    })).filter(msg => msg.parts.length > 0);

    formData.append('history', JSON.stringify(history));

    const apiUrl = process.env.NODE_ENV === 'production'
      ? `${process.env.REACT_APP_BACKEND_URL}/chat`
      : '/chat';

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      const botMessageParts = [{ type: 'text', data: data.reply }];

      if (data.downloadUrl) {
        botMessageParts.push({ type: 'file', data: data.downloadUrl, filename: 'Report.pdf' });
      }

      const botMessage = { parts: botMessageParts, sender: 'bot' };
      const finalMessages = [...updatedMessages, botMessage];
      updateConversation(currentConversationId, { messages: finalMessages });
    } catch (error) {
      console.error('Error sending message:', error);
      let errorMessage = 'Sorry, something went wrong.';
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        errorMessage = 'Unable to connect to the backend. Please make sure the backend server is running.';
      }
      const errorBotMessage = { parts: [{ type: 'text', data: errorMessage }], sender: 'bot' };
      const finalMessages = [...updatedMessages, errorBotMessage];
      updateConversation(currentConversationId, { messages: finalMessages });
    } finally {
      setLoading(false);
    }
  };

  const currentMessages = getCurrentConversation()?.messages || [];

  return (
    <div className="App">
      <div className="Sidebar">
        <ConversationList
          conversations={conversations}
          currentConversationId={currentConversationId}
          onSelectConversation={selectConversation}
          onNewConversation={startNewChat}
        />
      </div>
      <div className="ChatContainer">
        <header className="App-header">
          <div className="App-header-left">
            <img src={logo} className="App-logo" alt="logo" />
            <h1>Fuchsia Chat</h1>
          </div>
          <button
            className="SettingsButton"
            onClick={() => setSettingsOpen(true)}
            aria-label="Open settings"
          >
            <SettingIcon theme={theme} />
          </button>
        </header>
        <ChatBox messages={currentMessages} loading={loading} />
        <ChatInput onSendMessage={sendMessage} />
      </div>
      <Settings
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        theme={theme}
        onThemeChange={setTheme}
      />
    </div>
  );
}

export default App;